import { create } from 'zustand';
import { temporal } from 'zundo';
import { Node, Edge, XYPosition } from '@xyflow/react';
import { ConnectorType, NetworkZone, ServiceData, ActorData, ConnectorData, BusinessFlow } from '../types/diagram.types';
import { ALL_INITIAL_NODES } from '../data/initialNodes';
import { INITIAL_EDGES } from '../data/initialEdges';
import { BUSINESS_FLOWS } from '../data/businessFlows';
import { DATABASE_NODES, DB_ZONE_NODE, DB_EDGES } from '../data/initialDatabases';

interface PositionEntry extends XYPosition {
  width?: number;
  height?: number;
}

interface PositionMap {
  [nodeId: string]: PositionEntry;
}

interface UiState {
  actorVisibility: Record<string, boolean>;
  customActors: Node<ActorData>[];
}

interface DiagramStore {
  nodes: Node[];
  edges: Edge<ConnectorData>[];
  flows: BusinessFlow[];
  activeFlowId: string | null;
  selectedNodeId: string | null;
  zoneVisibility: Record<NetworkZone, boolean>;
  connectorTypeVisibility: Record<ConnectorType, boolean>;
  searchQuery: string;
  dbLayerVisible: boolean;
  dbConnections: Record<string, { host: string; port: number; dbname: string; user: string }>;
  projectServiceFilter: string[] | null;
  currentProjectId: string | null;
  actorVisibility: Record<string, boolean>;
  customActors: Node<ActorData>[];

  // Actions
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge<ConnectorData>[]) => void;
  setActiveFlow: (id: string | null) => void;
  selectNode: (id: string | null) => void;
  updateNodePosition: (id: string, pos: XYPosition) => void;
  updateNodeSize: (id: string, width: number, height: number) => void;
  toggleZone: (zoneId: NetworkZone) => void;
  toggleConnectorType: (type: ConnectorType) => void;
  setSearchQuery: (q: string) => void;
  addService: (node: Node) => void;
  updateService: (id: string, data: Partial<ServiceData>) => void;
  softDeleteService: (id: string) => void;
  toggleDbLayer: () => void;
  setDbConnection: (serviceId: string, config: { host: string; port: number; dbname: string; user: string }) => void;
  setProjectServiceFilter: (ids: string[] | null) => void;
  setCurrentProjectId: (id: string | null) => void;
  toggleActorVisibility: (id: string) => void;
  addCustomActor: (actor: Node<ActorData>) => void;
  removeCustomActor: (id: string) => void;
  getVisibleNodes: () => Node[];
  getHighlightedEdges: () => Edge<ConnectorData>[];
  resetPositions: () => void;
  // OSS-NETVIZ-3: серверный персист раскладки per-project.
  applyServerLayout: (positions: PositionMap, customServices: Node[]) => void;
  getServerLayoutSnapshot: () => { positions: PositionMap; customServices: Node[] };
}

const DEFAULT_ZONE_VISIBILITY: Record<NetworkZone, boolean> = {
  'zone-external': true,
  'zone-dmz': true,
  'zone-kafka': true,
  'zone-internal': true,
  'zone-partners': true,
};

const DEFAULT_CONNECTOR_VISIBILITY: Record<ConnectorType, boolean> = {
  REST: true,
  WebSocket: true,
  Kafka: true,
  PostgreSQL: true,
  Redis: true,
  TimescaleDB: true,
  S3: true,
  gRPC: true,
};

// Per-project localStorage keys
const getPositionsKey = (projectId: string | null | undefined): string =>
  projectId ? `vsai-node-positions-v6-${projectId}` : 'vsai-node-positions-v6';

const getUiStateKey = (projectId: string | null | undefined): string =>
  projectId ? `vsai-ui-state-v1-${projectId}` : 'vsai-ui-state-v1';

// Zone order (top→bottom) for directional handle assignment
const ZONE_ORDER: Record<string, number> = {
  'zone-external': 0,
  'zone-dmz': 1,
  'zone-internal': 2,
  'zone-partners': 3,
};

function getZoneHandles(
  srcZone: string | undefined,
  tgtZone: string | undefined,
): { sourceHandle?: string; targetHandle?: string } {
  const s = srcZone ? (ZONE_ORDER[srcZone] ?? 2) : 2;
  const t = tgtZone ? (ZONE_ORDER[tgtZone] ?? 2) : 2;
  if (s === t) return { sourceHandle: 'right', targetHandle: 'left' }; // same zone → horizontal
  if (s < t)   return { sourceHandle: 'bottom', targetHandle: 'top' }; // going down
  return        { sourceHandle: 'top-out', targetHandle: 'bottom-in' }; // going up
}

function loadSavedPositions(projectId?: string | null): PositionMap {
  try {
    const raw = localStorage.getItem(getPositionsKey(projectId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function loadUiState(projectId?: string | null): UiState {
  try {
    const raw = localStorage.getItem(getUiStateKey(projectId));
    if (!raw) return { actorVisibility: {}, customActors: [] };
    const parsed = JSON.parse(raw) as Partial<UiState>;
    return {
      actorVisibility: parsed.actorVisibility ?? {},
      customActors: parsed.customActors ?? [],
    };
  } catch {
    return { actorVisibility: {}, customActors: [] };
  }
}

function applyPositions(nodes: Node[], positions: PositionMap): Node[] {
  return nodes.map(n => {
    const saved = positions[n.id];
    if (!saved) return n;
    const updated: Node = { ...n, position: { x: saved.x, y: saved.y } };
    if (saved.width !== undefined || saved.height !== undefined) {
      updated.style = {
        ...n.style,
        ...(saved.width  !== undefined ? { width:  saved.width  } : {}),
        ...(saved.height !== undefined ? { height: saved.height } : {}),
      };
    }
    return updated;
  });
}

function savePositions(nodes: Node[], projectId: string | null): void {
  const map: PositionMap = {};
  nodes.forEach(n => {
    const entry: PositionEntry = { x: n.position.x, y: n.position.y };
    if (typeof n.style?.width  === 'number') entry.width  = n.style.width;
    if (typeof n.style?.height === 'number') entry.height = n.style.height;
    map[n.id] = entry;
  });
  try {
    localStorage.setItem(getPositionsKey(projectId), JSON.stringify(map));
  } catch {
    // ignore
  }
}

function saveUiState(actorVisibility: Record<string, boolean>, customActors: Node<ActorData>[], projectId: string | null): void {
  try {
    localStorage.setItem(getUiStateKey(projectId), JSON.stringify({ actorVisibility, customActors }));
  } catch {
    // ignore
  }
}

const savedPositions = loadSavedPositions(null);
const initialNodes = applyPositions(ALL_INITIAL_NODES, savedPositions);
const initialUiState = loadUiState(null);

// Assign stable 1-based indices to all edges
const indexedInitialEdges: Edge<ConnectorData>[] = INITIAL_EDGES.map((e, i) => ({
  ...e,
  data: { ...e.data!, index: i + 1 },
}));

// OSS-NETVIZ-1: debounce записи истории, чтобы непрерывный drag (множество
// updateNodePosition) не создавал десятки шагов undo — схлопываем в один.
let temporalDebounce: ReturnType<typeof setTimeout> | null = null;

export const useDiagramStore = create<DiagramStore>()(
  temporal(
    (set, get) => ({
  nodes: initialNodes,
  edges: indexedInitialEdges,
  flows: BUSINESS_FLOWS,
  activeFlowId: null,
  selectedNodeId: null,
  zoneVisibility: { ...DEFAULT_ZONE_VISIBILITY },
  connectorTypeVisibility: { ...DEFAULT_CONNECTOR_VISIBILITY },
  searchQuery: '',
  dbLayerVisible: false,
  dbConnections: {},
  projectServiceFilter: null,
  currentProjectId: null,
  actorVisibility: initialUiState.actorVisibility,
  customActors: initialUiState.customActors,

  setNodes: (nodes) => {
    set({ nodes });
    savePositions(nodes, get().currentProjectId);
  },

  setEdges: (edges) => set({ edges }),

  setActiveFlow: (id) => set({ activeFlowId: id }),

  selectNode: (id) => set({ selectedNodeId: id }),

  updateNodePosition: (id, pos) => {
    set(state => {
      const nodes = state.nodes.map(n =>
        n.id === id ? { ...n, position: pos } : n
      );
      savePositions(nodes, state.currentProjectId);
      return { nodes };
    });
  },

  updateNodeSize: (id, width, height) => {
    set(state => {
      const nodes = state.nodes.map(n =>
        n.id === id ? { ...n, style: { ...n.style, width, height } } : n
      );
      savePositions(nodes, state.currentProjectId);
      return { nodes };
    });
  },

  toggleZone: (zoneId) =>
    set(state => ({
      zoneVisibility: {
        ...state.zoneVisibility,
        [zoneId]: !state.zoneVisibility[zoneId],
      },
    })),

  toggleConnectorType: (type) =>
    set(state => ({
      connectorTypeVisibility: {
        ...state.connectorTypeVisibility,
        [type]: !state.connectorTypeVisibility[type],
      },
    })),

  setSearchQuery: (q) => set({ searchQuery: q }),

  addService: (node) =>
    set(state => ({ nodes: [...state.nodes, node] })),

  updateService: (id, data) =>
    set(state => ({
      nodes: state.nodes.map(n =>
        n.id === id ? { ...n, data: { ...n.data, ...data } } : n
      ),
    })),

  softDeleteService: (id) =>
    set(state => ({
      nodes: state.nodes.map(n =>
        n.id === id ? { ...n, data: { ...n.data, status: 'deprecated' } } : n
      ),
    })),

  toggleDbLayer: () =>
    set(state => ({ dbLayerVisible: !state.dbLayerVisible })),

  setProjectServiceFilter: (ids) => set({ projectServiceFilter: ids }),

  setDbConnection: (serviceId, config) =>
    set(state => ({
      dbConnections: {
        ...state.dbConnections,
        [serviceId]: config,
      },
    })),

  setCurrentProjectId: (id) => {
    const savedPos = loadSavedPositions(id);
    const uiState = loadUiState(id);
    const allNodes = [...ALL_INITIAL_NODES, ...uiState.customActors];
    const restoredNodes = applyPositions(allNodes, savedPos);
    set({
      currentProjectId: id,
      nodes: restoredNodes,
      actorVisibility: uiState.actorVisibility,
      customActors: uiState.customActors,
    });
  },

  toggleActorVisibility: (id) => {
    set(state => {
      const current = state.actorVisibility[id] !== false;
      const actorVisibility = { ...state.actorVisibility, [id]: !current };
      saveUiState(actorVisibility, state.customActors, state.currentProjectId);
      return { actorVisibility };
    });
  },

  addCustomActor: (actor) => {
    set(state => {
      const customActors = [...state.customActors, actor];
      saveUiState(state.actorVisibility, customActors, state.currentProjectId);
      // Also save position
      const nodes = [...state.nodes, actor];
      savePositions(nodes, state.currentProjectId);
      return { customActors, nodes };
    });
  },

  removeCustomActor: (id) => {
    set(state => {
      const customActors = state.customActors.filter(a => a.id !== id);
      const nodes = state.nodes.filter(n => n.id !== id);
      saveUiState(state.actorVisibility, customActors, state.currentProjectId);
      savePositions(nodes, state.currentProjectId);
      return { customActors, nodes };
    });
  },

  getVisibleNodes: () => {
    const { nodes, customActors, zoneVisibility, dbLayerVisible, projectServiceFilter, actorVisibility } = get();
    const basePool = [...nodes, ...customActors.filter(ca => !nodes.some(n => n.id === ca.id))];
    const baseNodes = basePool.filter(n => {
      if (n.type === 'zone') return true; // zones always visible
      if (n.type === 'actor') {
        const zone = (n.data as any)?.zone as NetworkZone | undefined;
        if (zone && !zoneVisibility[zone]) return false;
        if (actorVisibility[n.id] === false) return false;
        return true;
      }
      if (n.type === 'service') {
        const zone = (n.data as any)?.zone as NetworkZone | undefined;
        if (zone && !zoneVisibility[zone]) return false;
        // Apply project filter: if filter is set with IDs, only show those services
        if (projectServiceFilter && projectServiceFilter.length > 0 && !projectServiceFilter.includes(n.id)) return false;
        return true;
      }
      const zone = (n.data as any)?.zone as NetworkZone | undefined;
      if (zone && !zoneVisibility[zone]) return false;
      return true;
    });

    if (dbLayerVisible) {
      return [...baseNodes, DB_ZONE_NODE, ...DATABASE_NODES];
    }
    return baseNodes;
  },

  getHighlightedEdges: () => {
    const { nodes, edges, activeFlowId, connectorTypeVisibility, dbLayerVisible } = get();

    // Build zone lookup: nodeId → zone string
    const nodeZone: Record<string, string | undefined> = {};
    nodes.forEach(n => {
      nodeZone[n.id] = (n.data as any)?.zone as string | undefined;
    });

    const baseEdges = edges.filter(e => {
      const connType = (e.data as ConnectorData)?.type;
      if (connType && !connectorTypeVisibility[connType]) return false;
      return true;
    }).map(e => {
      const flowIds = (e.data as ConnectorData)?.flowIds ?? [];
      const isInFlow = activeFlowId ? flowIds.includes(activeFlowId) : false;
      const dimmed = activeFlowId !== null && !isInFlow;
      const { sourceHandle, targetHandle } = getZoneHandles(nodeZone[e.source], nodeZone[e.target]);
      return {
        ...e,
        sourceHandle: e.sourceHandle ?? sourceHandle,
        targetHandle: e.targetHandle ?? targetHandle,
        style: {
          ...e.style,
          opacity: dimmed ? 0.15 : 1,
        },
        animated: dimmed ? false : ((e.data as ConnectorData)?.type === 'Kafka' || (e.data as ConnectorData)?.type === 'WebSocket'),
      };
    });

    if (dbLayerVisible) {
      const dbEdgesMapped = DB_EDGES.map(e => {
        const flowIds = (e.data as any)?.flowIds ?? [];
        const isInFlow = activeFlowId ? flowIds.includes(activeFlowId) : false;
        const dimmed = activeFlowId !== null && !isInFlow;
        return {
          ...e,
          style: {
            ...e.style,
            opacity: dimmed ? 0.15 : 1,
          },
          animated: false,
        };
      }) as Edge<ConnectorData>[];

      return [...baseEdges, ...dbEdgesMapped];
    }

    return baseEdges;
  },

  resetPositions: () => {
    const { currentProjectId } = get();
    try { localStorage.removeItem(getPositionsKey(currentProjectId)); } catch { /* ignore */ }
    set({ nodes: ALL_INITIAL_NODES });
  },

  // OSS-NETVIZ-3: применить серверную раскладку — добавить кастомные сервисы,
  // которых ещё нет на холсте, затем наложить сохранённые позиции на все узлы.
  applyServerLayout: (positions, customServices) => {
    set(state => {
      const existingIds = new Set(state.nodes.map(n => n.id));
      const toAdd = (customServices || []).filter(n => n && n.id && !existingIds.has(n.id));
      const merged = [...state.nodes, ...toAdd];
      return { nodes: applyPositions(merged, positions || {}) };
    });
  },

  // Снимок раскладки для сохранения на сервер: позиции всех узлов + узлы,
  // добавленные оператором drag&drop (data.custom === true).
  getServerLayoutSnapshot: () => {
    const { nodes } = get();
    const positions: PositionMap = {};
    nodes.forEach(n => {
      positions[n.id] = {
        x: n.position.x,
        y: n.position.y,
        ...(typeof n.width === 'number' ? { width: n.width } : {}),
        ...(typeof n.height === 'number' ? { height: n.height } : {}),
      };
    });
    const customServices = nodes.filter(n => (n.data as ServiceData | undefined)?.custom === true);
    return { positions, customServices };
  },
    }),
    {
      // Undo/redo отслеживает только топологию (узлы/рёбра), не UI-стейт
      // (поиск/выделение/видимость зон). limit ограничивает глубину истории.
      limit: 100,
      partialize: (state) => ({ nodes: state.nodes, edges: state.edges }),
      handleSet: (handleSet) => (pastState) => {
        if (temporalDebounce) clearTimeout(temporalDebounce);
        temporalDebounce = setTimeout(() => handleSet(pastState), 250);
      },
    }
  )
);
